import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Plus, Search, LayoutGrid, List, Trash2, Edit2, 
  ChevronRight, X, Image as ImageIcon, Star, 
  Eye, EyeOff, MapPin, Calendar, 
  Check, RefreshCcw, GripVertical, Clock, Info
} from 'lucide-react';
import '../../styles/admin.css';
import { fetchEventsAdmin, createEvent, updateEvent, deleteEvent } from '../../lib/events';
import { getFileUrl, pb } from '../../lib/pb';

function formatDateFr(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('fr-FR');
}

function formatDateRange(start, end) {
  const s = formatDateFr(start);
  const e = formatDateFr(end);
  if (s === e) return s;
  if (!e || e === '—') return `Dès le ${s}`;
  return `${s} au ${e}`;
}

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create');
  const [viewMode, setViewMode] = useState('list');
  const [filterStatus, setFilterStatus] = useState('all');
  const sentinelRef = useRef(null);

  async function load(targetPage = 1) {
    setError('');
    const isFirst = targetPage === 1;
    if (isFirst) { setLoading(true); setItems([]); } else { setLoadingMore(true); }
    try {
      const res = await fetchEventsAdmin(targetPage, 24, search);
      setTotalPages(res.totalPages || 1);
      if (isFirst) setItems(res.items || []);
      else setItems(prev => [...prev, ...(res.items || [])]);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      if (isFirst) setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => { load(1); setPage(1); }, [search]);
  useEffect(() => { if (page > 1) load(page); }, [page]);

  // Realtime
  useEffect(() => {
    let unsub = null;
    (async () => {
      try { unsub = await pb.collection('events').subscribe('*', () => load(1)); } catch(e) {}
    })();
    return () => { if(unsub) pb.collection('events').unsubscribe('*').catch(()=>{}); };
  }, []);

  const filteredItems = useMemo(() => {
    const now = new Date();
    return items.filter(ev => {
      if (filterStatus === 'published' && !ev.published) return false;
      if (filterStatus === 'draft' && ev.published) return false;
      
      const end = ev.end_at ? new Date(ev.end_at) : (ev.start_at ? new Date(ev.start_at) : null);
      if (filterStatus === 'upcoming' && end && end < now) return false;
      if (filterStatus === 'past' && end && end >= now) return false;

      return true;
    });
  }, [items, filterStatus]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || loading || loadingMore || page >= totalPages) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setPage(p => p + 1);
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading, loadingMore, page, totalPages]);

  const openCreate = () => { setMode('create'); setEditing({ title: '', slug: '', location: '', start_at: '', end_at: '', published: true, images: [] }); };
  const openEdit = (ev) => { setMode('edit'); setEditing(ev); };

  async function handleTogglePublished(ev) {
    try { await updateEvent(ev.id, { published: !ev.published }); load(1); } catch (e) {}
  }
  async function handleDelete(ev) {
    if (!window.confirm(`Supprimer l'événement "${ev.title}" ?`)) return;
    try { await deleteEvent(ev.id); load(1); } catch (e) { alert(e.message); }
  }

  return (
    <div className="events-page">
      <header className="proj-header">
        <div className="header-text">
          <h1>Agenda des Événements</h1>
          <p className="muted">Gérez vos salons, portes ouvertes et foires.</p>
        </div>
        <button className="btn-add-main" onClick={openCreate}>
          <Plus size={20} />
          <span>Créer un Événement</span>
        </button>
      </header>

      <div className="proj-filters">
        <div className="search-box">
          <Search size={18} />
          <input placeholder="Rechercher par titre ou lieu..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="filter-group">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">Tous les événements</option>
            <option value="published">Publiés</option>
            <option value="draft">Brouillons</option>
            <option value="upcoming">À venir (Futurs)</option>
            <option value="past">Archives (Passés)</option>
          </select>
        </div>
        <div className="view-switcher">
           <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
           <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
        </div>
      </div>

      <main className="proj-main">
        {loading && items.length === 0 ? (
          <div className="loading-state">Préparation des affiches...</div>
        ) : (
          <div className={`proj-content-view ${viewMode}`}>
            {viewMode === 'grid' ? (
              <div className="proj-grid">
                {filteredItems.map(ev => (
                  <div key={ev.id} className="proj-card-premium" onClick={() => openEdit(ev)}>
                    <div className="proj-card-img">
                      {ev.images && ev.images[0] ? <img src={getFileUrl(ev, ev.images[0])} alt={ev.title} /> : <div className="no-img"><ImageIcon size={32}/></div>}
                      <div className={`proj-card-badge ${ev.published ? 'ok' : 'none'}`}>
                        {ev.published ? <Eye size={12}/> : <EyeOff size={12}/>}
                      </div>
                    </div>
                    <div className="proj-card-info">
                       <span className="cat-tag">Agenda • {ev.location || 'France'}</span>
                       <h3>{ev.title}</h3>
                       <div className="proj-card-meta">
                          <span><Calendar size={12}/> {formatDateRange(ev.start_at, ev.end_at)}</span>
                       </div>
                       <div className="proj-card-footer">
                          <div className="stats">{new Date(ev.end_at || ev.start_at) < new Date() ? 'Événement passé' : 'Événement futur'}</div>
                          <ChevronRight size={16} className="arrow" />
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-card">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{paddingLeft: 20}}>Événement</th>
                      <th>Lieu</th>
                      <th>Période</th>
                      <th>Statut</th>
                      <th style={{textAlign: 'right', paddingRight: 20}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map(ev => (
                      <tr key={ev.id}>
                        <td style={{paddingLeft: 20}}>
                          <div className="row-flex" onClick={() => openEdit(ev)} style={{cursor: 'pointer'}}>
                            <div className="row-img-s">
                               {ev.images?.[0] ? <img src={getFileUrl(ev, ev.images[0])} /> : <ImageIcon size={14}/>}
                            </div>
                            <div className="row-text">
                               <div className="row-title">{ev.title}</div>
                               <div className="row-sub">{ev.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className="row-meta"><MapPin size={12} style={{opacity:0.5}}/> {ev.location || '—'}</span></td>
                        <td><span className="row-meta"><Calendar size={12} style={{opacity:0.5}}/> {formatDateRange(ev.start_at, ev.end_at)}</span></td>
                        <td>
                           <div className={`row-published ${ev.published ? 'ok' : ''}`} onClick={() => handleTogglePublished(ev)} style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6}}>
                              {ev.published ? <Eye size={16}/> : <EyeOff size={16}/>}
                              <span className="status-txt">{ev.published ? 'PUBLIC' : 'BROUILLON'}</span>
                           </div>
                        </td>
                        <td style={{textAlign: 'right', paddingRight: 20}}>
                           <div className="row-actions-group">
                              <button title="Éditer" className="btn-mini-edit" onClick={() => openEdit(ev)}>
                                 <Edit2 size={14} />
                              </button>
                              <button title="Supprimer" className="btn-mini-del" onClick={() => handleDelete(ev)}>
                                 <Trash2 size={14} />
                              </button>
                           </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div ref={sentinelRef} style={{ height: 40 }} />
          </div>
        )}
      </main>

      {editing && (
        <EventModal 
          mode={mode} 
          initial={editing}
          onClose={() => setEditing(null)} 
          onSaved={() => { setEditing(null); load(1); }} 
        />
      )}

      <style>{`
        .events-page { display: flex; flex-direction: column; gap: 20px; animation: fadeIn 0.4s ease; padding-bottom: 80px; }
        
        .proj-header { display: flex; justify-content: space-between; align-items: flex-end; }
        .proj-header h1 { font-size: 28px; font-weight: 900; margin: 0; color: #0f172a; }
        
        .btn-add-main { background: var(--admin-primary); border: none; font-weight: 800; border-radius: 16px; padding: 12px 20px; display: flex; align-items: center; gap: 10px; cursor: pointer; box-shadow: 0 10px 15px -3px rgba(234, 179, 8, 0.2); transition: all 0.2s; }

        .proj-filters { display: flex; gap: 15px; align-items: center; background: #fff; padding: 10px 15px; border-radius: 20px; border: 1px solid var(--admin-border); flex-wrap: wrap; }
        .search-box { flex: 1; display: flex; align-items: center; gap: 10px; padding: 0 10px; color: #94a3b8; min-width: 200px; }
        .search-box input { border: none; outline: none; width: 100%; font-size: 14px; font-weight: 600; color: #0f172a; background: transparent; }
        .filter-group select { background: #f1f5f9; border: none; border-radius: 12px; padding: 8px 15px; font-weight: 800; font-size: 13px; color: #0f172a; outline: none; }
        .view-switcher { display: flex; background: #f1f5f9; padding: 4px; border-radius: 12px; gap: 4px; }
        .view-switcher button { width: 36px; height: 36px; border-radius: 9px; border: none; background: none; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .view-switcher button.active { background: #fff; color: #0f172a; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

        /* GRID */
        .proj-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 20px; }
        .proj-card-premium { background: #fff; border: 1px solid var(--admin-border); border-radius: 28px; overflow: hidden; cursor: pointer; transition: all 0.3s; }
        .proj-card-premium:hover { transform: translateY(-5px); border-color: var(--admin-primary); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.05); }
        .proj-card-img { height: 220px; background: #f8fafc; position: relative; overflow: hidden; }
        .proj-card-img img { width: 100%; height: 100%; object-fit: cover; }
        .proj-card-badge { position: absolute; top: 12px; right: 12px; width: 28px; height: 28px; border-radius: 50%; background: #fff; display: flex; align-items: center; justify-content: center; }
        .proj-card-badge.ok { color: #10b981; }
        .proj-card-info { padding: 20px; }
        .cat-tag { font-size: 10px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
        .proj-card-info h3 { margin: 4px 0 10px 0; font-size: 18px; font-weight: 800; color: #0f172a; }
        .proj-card-meta { display: flex; gap: 15px; margin-bottom: 15px; }
        .proj-card-meta span { font-size: 11px; font-weight: 800; color: #64748b; display: flex; align-items: center; gap: 5px; }
        .proj-card-footer { display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f1f5f9; padding-top: 15px; }
        .stats { color: #94a3b8; font-size: 11px; font-weight: 800; }
        .arrow { color: #f1f5f9; }

        /* LIST PREMIUM */
        .proj-content-view.list .admin-card { background: transparent; border: none; box-shadow: none; padding: 0; }
        .proj-content-view.list .admin-table { border-collapse: separate; border-spacing: 0 10px; width: 100%; margin-top: -10px; }
        .proj-content-view.list .admin-table thead th { background: transparent; color: #94a3b8; font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px; border: none; padding: 15px 20px; }
        .proj-content-view.list .admin-table tbody tr { background: #fff; border-radius: 20px; transition: all 0.3s; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
        .proj-content-view.list .admin-table tbody tr:hover { transform: scale(1.005) translateY(-2px); box-shadow: 0 15px 30px -10px rgba(0,0,0,0.08); }
        .proj-content-view.list .admin-table td { border: none; padding: 15px 20px; }
        .proj-content-view.list .admin-table td:first-child { border-radius: 20px 0 0 20px; border-left: 4px solid transparent; }
        .proj-content-view.list .admin-table tbody tr:hover td:first-child { border-left-color: var(--admin-primary); }
        .proj-content-view.list .admin-table td:last-child { border-radius: 0 20px 20px 0; }

        .row-flex { display: flex; align-items: center; gap: 15px; }
        .row-img-s { width: 56px; height: 42px; border-radius: 12px; overflow: hidden; background: #f1f5f9; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
        .row-img-s img { width: 100%; height: 100%; object-fit: cover; }
        .row-title { font-weight: 900; font-size: 15px; color: #0f172a; margin-bottom: 2px; }
        .row-sub { font-size: 10px; font-weight: 800; color: #94a3b8; text-transform: uppercase; }
        .row-meta { font-size: 13px; font-weight: 800; color: #334155; display: flex; align-items: center; gap: 6px; }
        .row-published { background: #f1f5f9; padding: 6px 12px; border-radius: 10px; transition: all 0.2s; }
        .row-published.ok { background: #ecfdf5; color: #059669; }
        .status-txt { font-size: 10px; font-weight: 900; text-transform: uppercase; }
        
        .row-actions-group { display: flex; gap: 8px; justify-content: flex-end; }
        .btn-mini-edit, .btn-mini-del { width: 38px; height: 38px; border-radius: 12px; border: 1px solid transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .btn-mini-edit { background: #f0f9ff; color: #0ea5e9; border-color: #e0f2fe; }
        .btn-mini-del { background: #fff1f2; color: #f43f5e; border-color: #ffe4e6; }
        .btn-mini-edit:hover { background: #0ea5e9; color: #fff; }
        .btn-mini-del:hover { background: #f43f5e; color: #fff; }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        
        @media (max-width: 900px) {
          .proj-header { flex-direction: column; align-items: flex-start; gap: 15px; }
          .view-switcher { display: none; }
          .admin-table thead { display: none; }
          .proj-content-view.list .admin-table { border-spacing: 0 15px; }
          .admin-table tr { display: block; border-radius: 24px; }
          .admin-table td { display: block; border: none; padding: 10px 20px !important; text-align: left !important; }
        }
      `}</style>
    </div>
  );
}

function EventModal({ mode, initial, onClose, onSaved }) {
    const [tab, setTab] = useState('infos');
    const [saving, setSaving] = useState(false);
    
    const [title, setTitle] = useState(initial.title || '');
    const [slug, setSlug] = useState(initial.slug || '');
    const [location, setLocation] = useState(initial.location || '');
    const [startAt, setStartAt] = useState(initial.start_at ? initial.start_at.slice(0,10) : '');
    const [endAt, setEndAt] = useState(initial.end_at ? initial.end_at.slice(0,10) : '');
    const [description, setDescription] = useState(initial.description || '');
    const [published, setPublished] = useState(initial.published ?? true);

    const [newFiles, setNewFiles] = useState([]);
    const [previews, setPreviews] = useState(initial.images?.map(img => getFileUrl(initial, img)) || []);

    const slugify = (v) => v.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    const handleSave = async (e) => {
        e.preventDefault(); setSaving(true);
        const fd = new FormData();
        fd.append('title', title); fd.append('slug', slug);
        fd.append('location', location); fd.append('start_at', startAt); fd.append('end_at', endAt);
        fd.append('description', description); fd.append('published', published);
        
        // Keep existing images (this approach varies by backend needs, 
        // assuming PocketBase update with existing names or new files)
        const payloadImages = initial.id 
          ? [...(initial.images || []), ...newFiles] 
          : newFiles;

        if (newFiles.length) {
           newFiles.forEach(f => fd.append('images', f));
        }

        try {
            if (initial.id) await updateEvent(initial.id, fd);
            else await createEvent(fd);
            onSaved();
        } catch(e) { alert(e.message); } finally { setSaving(false); }
    };

    return (
        <div className="modal-overlay-s" onClick={e => e.target.className === 'modal-overlay-s' && onClose()}>
            <div className="modal-content-l">
                <div className="modal-s-top">
                    <div className="modal-s-tabs">
                        <button className={tab === 'infos' ? 'active' : ''} onClick={() => setTab('infos')}>L'Événement</button>
                        <button className={tab === 'media' ? 'active' : ''} onClick={() => setTab('media')}>Affiches</button>
                        <button className={tab === 'config' ? 'active' : ''} onClick={() => setTab('config')}>Diffusion</button>
                    </div>
                    <button className="s-close" onClick={onClose}><X size={20}/></button>
                </div>
                
                <form className="modal-s-body" onSubmit={handleSave}>
                    {tab === 'infos' ? (
                        <div className="s-form">
                            <div className="s-field">
                                <label>Nom de l'événement</label>
                                <input value={title} onChange={e => { setTitle(e.target.value); if(!initial.slug) setSlug(slugify(e.target.value)); }} required />
                            </div>
                            <div className="s-field">
                                <label>Lieu / Adresse</label>
                                <input value={location} onChange={e => setLocation(e.target.value)} placeholder="ex: Parc des Expos, Reims" />
                            </div>
                            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:15}}>
                                <div className="s-field"><label>Date de début</label><input type="date" value={startAt} onChange={e => setStartAt(e.target.value)} required /></div>
                                <div className="s-field"><label>Date de fin</label><input type="date" value={endAt} onChange={e => setEndAt(e.target.value)} /></div>
                            </div>
                            <div className="s-field"><label>Texte descriptif</label><textarea value={description} onChange={e => setDescription(e.target.value)} style={{height:100}}/></div>
                        </div>
                    ) : tab === 'media' ? (
                        <div className="s-media">
                             <div className="s-img-grid">
                                {previews.map((s, i) => (
                                    <div key={i} className="s-img-item">
                                        <img src={s} /><button type="button" onClick={() => setPreviews(p => p.filter((_, idx) => idx !== i))}>×</button>
                                    </div>
                                ))}
                                <label className="s-img-add"><Plus size={24}/><input type="file" multiple hidden accept="image/*" onChange={e => {
                                    const fs = Array.from(e.target.files);
                                    setNewFiles(p => [...p, ...fs]);
                                    setPreviews(p => [...p, ...fs.map(f => URL.createObjectURL(f))]);
                                }} /></label>
                             </div>
                        </div>
                    ) : (
                        <div className="s-form">
                             <div className="s-field">
                                <label>Slug (URL interne)</label>
                                <input value={slug} onChange={e => setSlug(e.target.value)} />
                             </div>
                             <div className="s-toggle">
                                <div style={{display:'flex', alignItems:'center', gap:12}}>
                                   <div style={{width:40, height:40, borderRadius:12, background:'#f0fdf4', color:'#16a34a', display:'flex', alignItems:'center', justifyCenter:'center'}}><Eye size={20}/></div>
                                   <div><div style={{fontWeight:800}}>Visibilité Publique</div><div style={{fontSize:11, color:'#94a3b8'}}>Afficher sur le site</div></div>
                                </div>
                                <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                             </div>
                             <div className="s-info-box">
                                <Info size={16}/>
                                <span>L'ordre des événements est géré par la date (les plus récents en premier).</span>
                             </div>
                        </div>
                    )}
                    
                    <div className="modal-s-footer">
                        {initial.id && <button type="button" className="btn-del-team" onClick={async () => { if(window.confirm('Supprimer cet événement ?')){ await deleteEvent(initial.id); onSaved(); }}}><Trash2 size={16}/></button>}
                        <div style={{flex:1}}></div>
                        <button type="submit" className="btn-submit-team" disabled={saving}>{saving ? 'Sauvegarde...' : 'Enregistrer l\'Évènement'}</button>
                    </div>
                </form>
            </div>
            <style>{`
                .modal-overlay-s { position: fixed; inset: 0; background: rgba(15,23,42,0.4); backdrop-filter: blur(10px); z-index: 20000; display: flex; align-items: center; justify-content: center; padding: 20px; }
                .modal-content-l { background: #fff; width: 100%; max-width: 600px; border-radius: 32px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
                .modal-s-top { padding: 20px; border-bottom: 2px solid #f8fafc; display: flex; justify-content: space-between; align-items: center; }
                .modal-s-tabs { display: flex; gap: 5px; background: #f1f5f9; padding: 4px; border-radius: 12px; }
                .modal-s-tabs button { border: none; background: none; padding: 8px 18px; border-radius: 9px; font-weight: 800; font-size: 13px; color: #64748b; cursor: pointer; transition: all 0.2s; }
                .modal-s-tabs button.active { background: #fff; color: #0f172a; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
                .s-close { background: #f1f5f9; border: none; width: 36px; height: 36px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #64748b; }
                
                .modal-s-body { padding: 25px; max-height: 75vh; overflow-y: auto; }
                .s-form { display: grid; gap: 20px; }
                .s-field { display: grid; gap: 8px; }
                .s-field label { font-size: 11px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
                .s-field input, .s-field select, .s-field textarea { background: #f8fafc; border: 1.5px solid #e2e8f0; padding: 12px 15px; border-radius: 16px; font-weight: 700; color: #0f172a; outline: none; transition: all 0.2s; }
                .s-field input:focus { border-color: var(--admin-primary); background: #fff; box-shadow: 0 0 0 4px rgba(234, 179, 8, 0.1); }
                
                .s-img-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
                .s-img-item { aspect-ratio: 1; border-radius: 16px; overflow: hidden; position: relative; border: 1px solid #f1f5f9; }
                .s-img-item img { width: 100%; height: 100%; object-fit: cover; }
                .s-img-item button { position: absolute; top: 6px; right: 6px; background: rgba(0,0,0,0.5); color: #fff; border: none; width: 24px; height: 24px; border-radius: 50%; cursor: pointer; backdrop-filter: blur(4px); }
                .s-img-add { aspect-ratio: 1; border: 2.5px dashed #e2e8f0; border-radius: 16px; display: flex; align-items: center; justify-content: center; color: #cbd5e1; cursor: pointer; transition: all 0.2s; }
                .s-img-add:hover { border-color: var(--admin-primary); color: var(--admin-primary); background: #fffbeb; }

                .modal-s-footer { display: flex; align-items: center; gap: 15px; margin-top: 25px; border-top: 2px solid #f8fafc; padding-top: 20px; }
                .btn-del-team { width: 48px; height: 48px; border-radius: 16px; background: #fff1f2; color: #f43f5e; border: none; cursor: pointer; transition: all 0.2s; }
                .btn-del-team:hover { background: #f43f5e; color: #fff; }
                .btn-submit-team { flex: 1; height: 52px; background: #0f172a; color: #fff; border: none; border-radius: 16px; font-weight: 900; cursor: pointer; transition: all 0.2s; box-shadow: 0 10px 15px -3px rgba(15,23,42,0.1); }
                .btn-submit-team:hover { transform: translateY(-2px); box-shadow: 0 20px 25px -5px rgba(15,23,42,0.15); }
                .btn-submit-team:disabled { opacity: 0.5; cursor: not-allowed; }

                .s-toggle { display: flex; justify-content: space-between; align-items: center; background: #f8fafc; padding: 18px; border-radius: 20px; border: 1.5px solid #f1f5f9; }
                .s-toggle input[type="checkbox"] { width: 44px; height: 24px; appearance: none; background: #cbd5e1; border-radius: 20px; position: relative; cursor: pointer; outline: none; transition: all 0.3s; }
                .s-toggle input[type="checkbox"]:checked { background: #10b981; }
                .s-toggle input[type="checkbox"]::before { content:''; position: absolute; width: 18px; height: 18px; background: #fff; border-radius: 50%; top: 3px; left: 3px; transition: all 0.3s; }
                .s-toggle input[type="checkbox"]:checked::before { left: 23px; }

                .s-info-box { display: flex; align-items: center; gap: 12px; background: #f0f9ff; color: #0369a1; padding: 15px; border-radius: 16px; font-size: 12px; font-weight: 700; line-height: 1.4; border: 1px solid #e0f2fe; }
            `}</style>
        </div>
    );
}
